// src/components/seo/EventSoftwarePage.jsx
//
// Public, indexable event-management landing pages:
//   /event-management-software            (main page)
//   /event-registration-software
//   /event-ticketing-software
//   /nonprofit-event-management-software
//
// All copy lives in src/seo/eventPages.js. The server sends these paths with
// page-specific <head> tags + crawler HTML (publicPageRoutes.js, fed by
// scripts/buildSeoPages.mjs); useSeo below keeps the same tags right during
// client-side navigation between them.

import React, { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { postJson } from '../../services/http';
import {
  EVENT_PAGES,
  OG_IMAGE,
  SIGNUP_URL,
  DOCS_URL,
  pageBySlug,
  pageUrl,
  buildJsonLd,
} from '../../seo/eventPages';
import './EventSoftwarePage.css';

const LOGO = `${process.env.PUBLIC_URL}/on-screen-logo-white-bezel-h160.png`;

function upsertHead(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

// Title, description, canonical, OG/Twitter and JSON-LD for `page`; restores
// whatever was there before on unmount so /about gets its own tags back.
function useSeo(page) {
  useEffect(() => {
    if (!page) return undefined;
    const url = pageUrl(page);
    const restore = [];
    const prevTitle = document.title;
    document.title = page.title;

    const setMeta = (attr, key, content) => {
      const el = upsertHead(`meta[${attr}="${key}"]`, () => {
        const m = document.createElement('meta');
        m.setAttribute(attr, key);
        return m;
      });
      const prev = el.getAttribute('content');
      el.setAttribute('content', content);
      restore.push(() => (prev == null ? el.remove() : el.setAttribute('content', prev)));
    };

    setMeta('name', 'description', page.description);
    setMeta('property', 'og:title', page.title);
    setMeta('property', 'og:description', page.description);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:image', OG_IMAGE);
    setMeta('name', 'twitter:title', page.title);
    setMeta('name', 'twitter:description', page.description);

    const canonical = upsertHead('link[rel="canonical"]', () => {
      const l = document.createElement('link');
      l.setAttribute('rel', 'canonical');
      return l;
    });
    const prevCanonical = canonical.getAttribute('href');
    canonical.setAttribute('href', url);

    // index.html (and the server-injected head) ship their own JSON-LD; set it
    // aside while this page owns the head.
    const parked = [...document.head.querySelectorAll('script[type="application/ld+json"]:not([data-seo])')];
    parked.forEach((s) => s.setAttribute('type', 'application/ld+json-parked'));
    const added = buildJsonLd(page).map((obj) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.dataset.seo = 'event-page';
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      document.title = prevTitle;
      restore.forEach((fn) => fn());
      if (prevCanonical) canonical.setAttribute('href', prevCanonical);
      added.forEach((s) => s.remove());
      parked.forEach((s) => s.setAttribute('type', 'application/ld+json'));
    };
  }, [page]);
}

function DocLink({ href, children = 'See how it works' }) {
  if (!href) return null;
  return (
    <a className="osep-doclink" href={href}>
      {children} <span aria-hidden="true">→</span>
    </a>
  );
}

export default function EventSoftwarePage({ slug: slugProp }) {
  const params = useParams();
  const page = pageBySlug(slugProp || params.slug);
  useSeo(page);

  // Break out of the app shell's .main / .main-content sizing (same as /shops).
  useEffect(() => {
    document.body.classList.add('osep-page-active');
    return () => document.body.classList.remove('osep-page-active');
  }, []);

  // Anonymous visit beacon for the super-admin Site Visits card.
  useEffect(() => {
    if (page) postJson('/api/tracking/site-visit', { path: `/${page.slug}` }).catch(() => {});
  }, [page]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  if (!page) return <Navigate to="/event-management-software" replace />;

  const related = page.related.map(pageBySlug).filter(Boolean);

  return (
    <div className="osep">
      <header className="osep-top">
        <Link to="/about" className="osep-logo" aria-label="On-Screen home">
          <img src={LOGO} alt="On-Screen" height="40" />
        </Link>
        <nav aria-label="Event features" className="osep-nav">
          {EVENT_PAGES.map((p) => (
            <Link
              key={p.slug}
              to={`/${p.slug}`}
              className={p.slug === page.slug ? 'is-active' : undefined}
              aria-current={p.slug === page.slug ? 'page' : undefined}
            >
              {p.navLabel}
            </Link>
          ))}
        </nav>
      </header>

      <main>
        <section className="osep-hero">
          <p className="osep-eyebrow">{page.eyebrow}</p>
          <h1>{page.h1}</h1>
          <p className="osep-intro">{page.intro}</p>
          <div className="osep-ctas">
            <a className="osep-btn osep-btn--primary" href={SIGNUP_URL}>
              Get started free
            </a>
            <a className="osep-btn osep-btn--ghost" href={DOCS_URL}>
              Read the user guide
            </a>
          </div>
          <p className="osep-note">
            Installs as an app on iOS, Android, Windows, and macOS — no app store needed.
          </p>
        </section>

        <section className="osep-section" aria-labelledby="osep-workflow-title">
          <h2 id="osep-workflow-title">{page.workflowTitle}</h2>
          <ol className="osep-steps">
            {page.workflow.map((step, i) => (
              <li key={step.title} className="osep-step">
                <span className="osep-step-num" aria-hidden="true">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                  <DocLink href={step.doc} />
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="osep-section osep-section--tint" aria-labelledby="osep-highlights-title">
          <h2 id="osep-highlights-title">{page.highlights.title}</h2>
          <div className="osep-grid">
            {page.highlights.items.map((item) => (
              <article key={item.title} className="osep-card">
                <h3>{item.title}</h3>
                <p>{item.body}</p>
                <DocLink href={item.doc}>Learn more</DocLink>
              </article>
            ))}
          </div>
        </section>

        <section className="osep-section" aria-labelledby="osep-faq-title">
          <h2 id="osep-faq-title">Questions</h2>
          <div className="osep-faq">
            {page.faq.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="osep-section osep-section--tint" aria-labelledby="osep-related-title">
          <h2 id="osep-related-title">Related</h2>
          <ul className="osep-related">
            {related.map((p) => (
              <li key={p.slug}>
                <Link to={`/${p.slug}`}>
                  <strong>{p.relatedTitle}</strong>
                  <span>{p.intro}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section className="osep-final">
          <h2>Set up your next event</h2>
          <p>Start with the parts you need. Add the rest when you’re ready.</p>
          <a className="osep-btn osep-btn--primary" href={SIGNUP_URL}>
            Get started free
          </a>
        </section>
      </main>

      <footer className="osep-footer">
        <a href="/about">About On-Screen</a>
        <a href="/shops">For shops</a>
        <a href={DOCS_URL}>Documentation</a>
        <a href="/about-me">About the founder</a>
        <a href="/termsofuse">Terms</a>
        <a href="/privacypolicy">Privacy</a>
        <span>© SE3D Inc.</span>
      </footer>
    </div>
  );
}
